import React, { useState, useEffect, useRef, Fragment, useMemo } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { Device } from '@twilio/voice-sdk';
import { toast } from 'react-toastify';
import styled from '@emotion/styled';
import { FaHeadphonesAlt, FaPhone, FaPhoneSlash } from 'react-icons/fa';
import { AiOutlineAudio, AiOutlineAudioMuted } from 'react-icons/ai';
import { MdAddCall } from 'react-icons/md';
import { connect } from 'react-redux';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import {
    bargeIntoCallAction,
    clearActiveCall,
    endCallAction,
    listenToCallAction,
    setActiveCall,
} from '@/_redux/actions/ActionCallCenter';
import { callCenterApi } from '@/_services/api/callCenter';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import dayjs from 'dayjs';
import { IoPerson } from 'react-icons/io5';
const AUTO_REFRESH_INTERVAL = 5000;
const CallContainer = styled.div`
    .call-visualization {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100px;
        margin: 20px 0;
        position: relative;
    }

    .sound-wave {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        height: 60px;
    }

    .bar {
        width: 4px;
        background: #007bff;
        border-radius: 2px;
        animation: soundWave 3s ease-in-out infinite;
    }

    @keyframes soundWave {
        0%,
        100% {
            height: 10px;
        }
        50% {
            height: 60px;
        }
    }

    .call-status {
        text-align: center;
        margin: 20px 0;
    }

    .call-timer {
        font-size: 24px;
        font-weight: bold;
        color: #28a745;
        margin: 10px 0;
    }

    .phone-ui {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-radius: 20px;
        padding: 10px;
        color: white;
        text-align: center;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    }

    .call-controls {
        display: flex;
        justify-content: center;
        gap: 20px;
        margin-top: 30px;
    }

    .call-btn {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
            transform: scale(1.1);
        }
    }

    .hangup-btn {
        background: #dc3545;
        color: white;
    }

    .leave-btn {
        background: #ffa500;
        color: white;
    }

    .barge-btn {
        background: #28a745;
        color: white;
    }

    .status-indicator {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-right: 8px;

        &.connecting {
            background: #ffc107;
            animation: pulse 1.5s infinite;
        }

        &.connected {
            background: #28a745;
            animation: pulse 2s infinite;
        }

        &.disconnected {
            background: #dc3545;
        }
    }

    @keyframes pulse {
        0% {
            transform: scale(0.8);
            opacity: 0.5;
        }
        50% {
            transform: scale(1.2);
            opacity: 1;
        }
        100% {
            transform: scale(0.8);
            opacity: 0.5;
        }
    }
`;
const formatCallDuration = (startedAt, endedAt) => {
  if (!startedAt || !endedAt) return '0:00';
  
  const start = dayjs(startedAt);
  const end = dayjs(endedAt);
  const diffInSeconds = end.diff(start, 'second');
  
  const hours = Math.floor(diffInSeconds / 3600);
  const minutes = Math.floor((diffInSeconds % 3600) / 60);
  const seconds = diffInSeconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};
function ActiveCallModal({
    show,
    onClose = () => {},
    activeCallId,
    callCenter,
    onHangup,
    onListenIn,
    onBargeIn,
    clearActiveCall,
    hangupLoading = false,
    listenLoading = false,
    bargeLoading = false,
}) {
    const [isMuted, setIsMuted] = useState(false);
    const { callConnection } = callCenter || {};
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [callDuration, setCallDuration] = useState(0);
    const deviceRef = useRef(null);
    const callRef = useRef(null);
    const timerRef = useRef(null);
    const [activeCall, setActiveCall] = useState({});

    const fetchActiveCall = async () => {
        try {
            const callid = activeCallId || activeCall?._id;
            if (!callid) return;
            const callStatus = activeCall?.callStatus;
            if (callStatus === 'completed') {
                return;
            }
            const response = await callCenterApi.getCall(callid);
            setActiveCall(response);
        } catch (error) {
            console.error('Error fetching active:', error);
        }
    };
    useEffect(() => {
        fetchActiveCall();
        let interval;
        interval = setInterval(() => {
            fetchActiveCall();
        }, AUTO_REFRESH_INTERVAL);

        return () => {
            if (interval) clearInterval(interval);
        };
    }, []);
    useEffect(() => {
        if (show && activeCall && activeCall?.startedAt && activeCall?.callStatus === 'in_progress') {
            const startTime = new Date(activeCall.startedAt);
            const updateTimer = () => {
                const now = new Date();
                const duration = Math.floor((now - startTime) / 1000);
                setCallDuration(duration);
            };

            updateTimer();
            timerRef.current = setInterval(updateTimer, 1000);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [activeCall, show]);

    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        if (callConnection && callConnection.token && show) {
            initializeTwilioDevice();
        }

        return () => {
            cleanupTwilioDevice();
        };
    }, [callConnection, show]);

    const initializeTwilioDevice = async () => {
        console.log('Initializing Twilio Device for active call');
        setConnectionStatus('connecting');

        try {
            const device = new Device(callConnection.token, {
                logLevel: 1,
                codecPreferences: ['opus', 'pcmu'],
            });

            device.on('registered', async () => {
                console.log('Twilio Device Registered for active call');

                if (!callConnection.conferenceName) {
                    console.error('Missing conference name');
                    toast.error('Connection failed: Missing conference details');
                    return;
                }

                const params = {
                    conferenceName: callConnection.conferenceName,
                    muted: 'false', // Start unmuted by default
                };

                try {
                    const call = await device.connect({ params });
                    callRef.current = call;

                    call.on('accept', () => {
                        console.log('Call accepted (connected to conference)');
                        setConnectionStatus('connected');
                        setIsMuted(false);
                    });

                    call.on('disconnect', () => {
                        console.log('Call disconnected');
                        setConnectionStatus('disconnected');
                    });

                    call.on('error', (err) => {
                        console.error('Call error:', err);
                        toast.error('Call connection error');
                    });
                } catch (err) {
                    console.error('Error connecting device:', err);
                    toast.error('Failed to connect: ' + err.message);
                }
            });

            device.on('error', (error) => {
                console.error('Device error:', error);
                toast.error(`Connection error: ${error.message}`);
                setConnectionStatus('disconnected');
            });

            device.on('connect', (conn) => {
                console.log('Connected to conference');
                toast.success('Connected to call audio');
                setConnectionStatus('connected');
            });

            device.register();
            deviceRef.current = device;
        } catch (err) {
            console.error('Failed to initialize device:', err);
            toast.error('Failed to initialize audio device');
            setConnectionStatus('disconnected');
        }
    };

    const cleanupTwilioDevice = () => {
        // Disconnect the active call first
        if (callRef.current) {
            console.log('Disconnecting active call');
            try {
                callRef.current.disconnect();
            } catch (error) {
                console.error('Error disconnecting call:', error);
            }
            callRef.current = null;
        }
        // Destroy the device
        if (deviceRef.current) {
            console.log('Destroying Twilio Device');
            try {
                deviceRef.current.destroy();
            } catch (error) {
                console.error('Error destroying device:', error);
            }
            deviceRef.current = null;
        }

        setConnectionStatus('disconnected');
        setIsMuted(false);
    };

    const handleToggleMute = () => {
        if (callRef.current) {
            if (callRef.current.isMuted()) {
                callRef.current.mute(false);
                setIsMuted(false);
                toast.info('Call unmuted');
            } else {
                callRef.current.mute(true);
                setIsMuted(true);
                toast.info('Call muted');
            }
        } else {
            console.warn('No active call to mute');
        }
    };
    const handleLeaveCall = () => {
        clearActiveCall();
        cleanupTwilioDevice();
        toast.info('You left the call');
    };

    const handleHangup = async () => {
        clearActiveCall();
        cleanupTwilioDevice();
        await onHangup(activeCall);
    };

    const handleClose = () => {
        clearActiveCall();
        cleanupTwilioDevice();
        onClose();
    };

    const renderSoundWave = () => {
        const bars = [];
        for (let i = 0; i < 20; i++) {
            const delay = i * 0.1;
            bars.push(
                <div
                    key={i}
                    className="bar"
                    style={{
                        animationDelay: `${delay}s`,
                        height: true ? '60px' : '10px',
                        background: true ? '#28a745' : '#fff',
                    }}
                />
            );
        }
        return bars;
    };
    const isActiveCall = activeCall?.callStatus === 'in_progress' || activeCall?.callStatus === 'ringing';
    const contactData = activeCall?.contactData || activeCall?.contact;
    const presetData = activeCall?.presetData || activeCall?.preset;
    const dialedByUserData = activeCall?.dialedByUserData || activeCall?.dialedByUser;

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <span className={`status-indicator ${connectionStatus}`} />
                    Active Call
                    <span
                        key={activeCall?.callStatus}
                        className={`badge ml-2 ${
                            activeCall?.callStatus === 'in_progress'
                                ? 'bg-success'
                                : activeCall?.callStatus === 'ringing'
                                  ? 'bg-warning'
                                  : 'bg-secondary'
                        }`}
                    >
                        {activeCall?.callStatus?.replace('_', ' ') || '-'}
                    </span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <CallContainer>
                    {/* Phone-style UI */}
                    <div className="phone-ui">
                        <h4 className="mb-3">
                            {contactData?.phone || '-'} ({contactData?.name || '-'})
                        </h4>
                        {isActiveCall ? (
                            <Fragment>
                                <div className="caller-info">
                                    {activeCall?.callStatus === 'ringing' ? (
                                        <Fragment>
                                            <div className="call-timer">Call is Ringing...</div>
                                        </Fragment>
                                    ) : (
                                        <Fragment>
                                            <div className="call-timer">{formatDuration(callDuration)}</div>
                                            <div className="call-visualization">
                                                <div className="sound-wave">{renderSoundWave()}</div>
                                            </div>
                                        </Fragment>
                                    )}
                                </div>
                                {activeCall?.callStatus && (
                                    <Fragment>
                                        {/* Call Status */}
                                        <div className="call-status">
                                            {/* <div className="mb-2">
                                                <strong>Status:</strong>{' '}
                                                <span
                                                    className={`text-${connectionStatus === 'connected' ? 'success' : connectionStatus === 'connecting' ? 'warning' : 'danger'}`}
                                                >
                                                    {connectionStatus === 'connected'
                                                        ? 'Connected to Call'
                                                        : connectionStatus === 'connecting'
                                                            ? 'Connecting...'
                                                            : 'Disconnected'}
                                                </span>
                                            </div> */}
                                            <div className="mb-2">
                                                <small className="text-light">
                                                    <strong>Preset:</strong> {presetData?.title || 'No preset'}
                                                </small>
                                            </div>
                                        </div>{' '}
                                    </Fragment>
                                )}
                            </Fragment>
                        ) : (
                            <p>
                                {activeCall?.callStatus === 'completed' ? (
                                    <Fragment>
                                        <h2>
                                            <span>Call Ended :</span>
                                            <span className="ml-2">
                                                {formatCallDuration(activeCall?.startedAt, activeCall?.endedAt)}
                                            </span>
                                        </h2>
                                    </Fragment>
                                ) : (
                                    'The call is not active'
                                )}
                            </p>
                        )}

                        {/* Call Controls */}
                        <div className="call-controls">
                            {connectionStatus === 'connected' && (
                                <BootstrapToolTip text={`${isMuted ? 'Unmute' : 'Mute'}`}>
                                    <button
                                        className={`call-btn mute-btn`}
                                        onClick={handleToggleMute}
                                        disabled={connectionStatus !== 'connected'}
                                    >
                                        {isMuted ? <AiOutlineAudioMuted /> : <AiOutlineAudio />}
                                    </button>
                                </BootstrapToolTip>
                            )}
                            {activeCall?.callStatus !== 'completed' && (
                                <BootstrapToolTip text="Hangup">
                                    <button
                                        className="call-btn hangup-btn"
                                        onClick={() => {
                                            handleHangup(activeCall);
                                            fetchActiveCall();
                                        }}
                                        disabled={hangupLoading}
                                        type="button"
                                    >
                                        {hangupLoading ? <Spinner animation="border" size="sm" /> : <FaPhone />}
                                    </button>
                                </BootstrapToolTip>
                            )}

                            {/* {connectionStatus === 'connected' && (
                                <BootstrapToolTip text="Leave Call">
                                    <button
                                        className="call-btn leave-btn"
                                        onClick={() => {
                                            handleLeaveCall(activeCall);
                                            fetchActiveCall();
                                        }}
                                        disabled={hangupLoading}
                                        type="button"
                                    >
                                        <FaPhoneSlash />
                                    </button>
                                </BootstrapToolTip>
                            )} */}
                        </div>
                    </div>

                    {/* Additional Info */}
                    <div className="mt-4">
                        <div className="row">
                            <div className="col-md-6">
                                <div className="card">
                                    <div className="card-body">
                                        <strong>Call Information:</strong>
                                        <p className="mb-1">
                                            <strong>Dialed At:</strong>{' '}
                                            {activeCall?.createdAt
                                                ? new Date(activeCall?.createdAt).toLocaleString()
                                                : '-'}
                                        </p>
                                        <p className="mb-1">
                                            <strong>Dialed by:</strong> {dialedByUserData?.name || '-'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card">
                                    <div className="card-body">
                                        <strong>Connection Status:</strong>
                                        <p className="mb-1">
                                            <strong>Conference:</strong> {callConnection?.conferenceName || 'N/A'}
                                        </p>
                                        <p className="mb-0">
                                            <strong>Audio:</strong>{' '}
                                            <span
                                                className={`badge bg-${connectionStatus === 'connected' ? 'success' : connectionStatus === 'connecting' ? 'warning' : 'danger'}`}
                                            >
                                                {connectionStatus}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </CallContainer>
            </Modal.Body>
            <Modal.Footer>
                <div className="mt-3">
                    {activeCall?.callStatus === 'in_progress' && (
                        <Fragment>
                            <Button
                                variant="outline-secondary"
                                onClick={() => {
                                    onBargeIn(activeCall);
                                }}
                                disabled={bargeLoading}
                            >
                                {listenLoading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-1" />
                                        Connecting...
                                    </>
                                ) : (
                                    <>
                                        <MdAddCall />
                                        <span className="ml-2">Barge In</span>
                                    </>
                                )}
                            </Button>
                            {/* <Button
                                variant="outline-secondary"
                                onClick={() => {
                                    onListenIn(activeCall);
                                }}
                                disabled={listenLoading || connectionStatus !== 'disconnected'}
                                className="ml-3"
                            >
                                {listenLoading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-1" />
                                        Connecting...
                                    </>
                                ) : (
                                    <>
                                        <IoPerson />
                                        <span className="ml-2">Coach In</span>
                                    </>
                                )}
                            </Button> */}
                        </Fragment>
                    )}

                    <Button variant="secondary" onClick={handleClose} className="ml-3">
                        Close
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

const mapStateToProps = (state) => ({
    callCenter: state.callCenter,
});
const mapDispatchToProps = {
    endCallAction,
    bargeIntoCallAction,
    listenToCallAction,
    setActiveCall,
    clearActiveCall,
};

const CalledTab = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <ActiveCallModal {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, mapDispatchToProps)(CalledTab);
